const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Generic API helper
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    headers,
    ...options,
  });

  if (!response.ok) {
    // Handle authentication errors gracefully
    if (response.status === 401 || response.status === 403) {
      // Only clear token if it's an auth error, but don't throw to avoid breaking the app
      console.warn('Authentication error on API call:', endpoint);
      // Don't automatically log out - let the auth context handle it
    }
    
    let errorMessage = `API call failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON, use status text
    }
    const error: any = new Error(errorMessage);
    error.response = { data: { message: errorMessage }, status: response.status };
    throw error;
  }

  return response.json();
};

// Athlete API calls
export const athleteAPI = {
  getAll: () => apiCall('/athletes'),
  getById: (id: string) => apiCall(`/athletes/${id}`),
  getMyAthlete: () => apiCall('/athletes/my-athlete'),
  getByIdForConnect: (id: string) => apiCall(`/athletes/connect/${id}`),
  create: (athlete: any) => apiCall('/athletes', {
    method: 'POST',
    body: JSON.stringify(athlete),
  }),
  addCoach: (athleteId: string) => apiCall(`/athletes/${athleteId}/add-coach`, {
    method: 'POST',
  }),
  removeCoach: (athleteId: string) => apiCall(`/athletes/${athleteId}/remove-coach`, {
    method: 'POST',
  }),
  update: (id: string, athlete: any) => apiCall(`/athletes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(athlete),
  }),
  delete: (id: string) => apiCall(`/athletes/${id}`, {
    method: 'DELETE',
  }),
  addWeight: (id: string, weight: number, date: string) => apiCall(`/athletes/${id}/weight`, {
    method: 'POST',
    body: JSON.stringify({ weight, date }),
  }),
  addHeight: (id: string, height: number, date: string) => apiCall(`/athletes/${id}/height`, {
    method: 'POST',
    body: JSON.stringify({ height, date }),
  }),
  updateWeight: (id: string, entryId: string, weight: number, date: string) => apiCall(`/athletes/${id}/weight/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify({ weight, date }),
  }),
  deleteWeight: (id: string, entryId: string) => apiCall(`/athletes/${id}/weight/${entryId}`, {
    method: 'DELETE',
  }),
  updateHeight: (id: string, entryId: string, height: number, date: string) => apiCall(`/athletes/${id}/height/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify({ height, date }),
  }),
  deleteHeight: (id: string, entryId: string) => apiCall(`/athletes/${id}/height/${entryId}`, {
    method: 'DELETE',
  }),
  updateGoal: (id: string, goalData: any) => apiCall(`/athletes/${id}/goals`, {
    method: 'POST',
    body: JSON.stringify(goalData),
  }),
};

// Training Session API calls
export const trainingSessionAPI = {
  getAll: (page?: number, limit?: number, all?: boolean, trainingType?: string) => {
    const params = new URLSearchParams();
    if (all) {
      params.append('all', 'true');
    } else {
      if (page !== undefined) params.append('page', page.toString());
      if (limit !== undefined) params.append('limit', limit.toString());
    }
    if (trainingType) params.append('trainingType', trainingType);
    const queryString = params.toString();
    return apiCall(`/training-sessions${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: string) => apiCall(`/training-sessions/${id}`),
  getByAthlete: (athleteId: string, page?: number, limit?: number, all?: boolean, trainingType?: string) => {
    const params = new URLSearchParams();
    if (all) {
      params.append('all', 'true');
    } else {
      if (page !== undefined) params.append('page', page.toString());
      if (limit !== undefined) params.append('limit', limit.toString());
    }
    if (trainingType) params.append('trainingType', trainingType);
    const queryString = params.toString();
    return apiCall(`/training-sessions/athlete/${athleteId}${queryString ? `?${queryString}` : ''}`);
  },
  create: (session: any) => apiCall('/training-sessions', {
    method: 'POST',
    body: JSON.stringify(session),
  }),
  addJump: (sessionId: string, jump: any) => apiCall(`/training-sessions/${sessionId}/jumps`, {
    method: 'POST',
    body: JSON.stringify(jump),
  }),
  addCompetitionJump: (sessionId: string, competitionJump: any) => apiCall(`/training-sessions/${sessionId}/competition-jumps`, {
    method: 'POST',
    body: JSON.stringify(competitionJump),
  }),
  addGroundTraining: (sessionId: string, groundTraining: any) => apiCall(`/training-sessions/${sessionId}/ground-trainings`, {
    method: 'POST',
    body: JSON.stringify(groundTraining),
  }),
  update: (id: string, session: any) => apiCall(`/training-sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(session),
  }),
  pause: (id: string) => apiCall(`/training-sessions/${id}/pause`, {
    method: 'PATCH',
  }),
  resume: (id: string) => apiCall(`/training-sessions/${id}/resume`, {
    method: 'PATCH',
  }),
  delete: (id: string) => apiCall(`/training-sessions/${id}`, {
    method: 'DELETE',
  }),
};

// Equipment API calls
export const equipmentAPI = {
  getAll: () => apiCall('/equipment'),
  getById: (id: string) => apiCall(`/equipment/${id}`),
  getByType: (type: 'suit' | 'skis' | 'shoes') => apiCall(`/equipment/type/${type}`),
  getByAthlete: (athleteId: string) => apiCall(`/equipment/athlete/${athleteId}`),
  getJumpCounts: () => apiCall('/equipment/jump-counts'),
  create: (equipment: any) => apiCall('/equipment', {
    method: 'POST',
    body: JSON.stringify(equipment),
  }),
  update: (id: string, equipment: any) => apiCall(`/equipment/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(equipment),
  }),
  delete: (id: string) => apiCall(`/equipment/${id}`, {
    method: 'DELETE',
  }),
};

// Banana Cup API calls
export const bananaCupAPI = {
  getAll: () => apiCall('/banana-cup'),
  getById: (id: string) => apiCall(`/banana-cup/${id}`),
  create: (bananaCup: any) => apiCall('/banana-cup', {
    method: 'POST',
    body: JSON.stringify(bananaCup),
  }),
  update: (id: string, bananaCupData: any) => apiCall(`/banana-cup/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(bananaCupData),
  }),
  updateCompetition: (id: string, competitionNumber: number, competitionData: any) => apiCall(`/banana-cup/${id}/competitions/${competitionNumber}`, {
    method: 'PATCH',
    body: JSON.stringify(competitionData),
  }),
  addJump: (id: string, competitionNumber: number, jump: any) => apiCall(`/banana-cup/${id}/competitions/${competitionNumber}/jumps`, {
    method: 'POST',
    body: JSON.stringify(jump),
  }),
  finalizeCompetition: (id: string, competitionNumber: number) => apiCall(`/banana-cup/${id}/competitions/${competitionNumber}/finalize`, {
    method: 'POST',
  }),
  delete: (id: string) => apiCall(`/banana-cup/${id}`, {
    method: 'DELETE',
  }),
};

// Auth API calls
export const authAPI = {
  linkAthlete: (athleteId: string) => apiCall('/auth/link-athlete', {
    method: 'POST',
    body: JSON.stringify({ athleteId }),
  }),
  updateProfile: (profileData: any) => apiCall('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),
};

// Chat API calls
export const chatAPI = {
  sendMessage: (message: string, conversationHistory: Array<{ role: string; content: string }> = []) => 
    apiCall('/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationHistory }),
    }),
  checkStatus: () => apiCall('/chat'),
};

// Stripe API calls
export const stripeAPI = {
  createCheckoutSession: (data: {
    plan: 'hs60' | 'hs90' | 'hs130' | 'hs240';
    billingPeriod: 'monthly' | 'yearly';
    athleteIds?: string[];
    useDiscount?: boolean;
  }) => apiCall('/stripe/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  createCoachSetupSession: (data: {
    plan: 'hs60' | 'hs90' | 'hs130' | 'hs240';
    billingPeriod: 'monthly' | 'yearly';
    athleteIds: string[];
    useDiscount?: boolean;
  }) => apiCall('/stripe/create-coach-setup-session', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getPricing: () => apiCall('/stripe/pricing'),
  getSubscriptionStatus: () => apiCall('/stripe/subscription-status'),
  cancelSubscription: (athleteId: string) => apiCall('/stripe/cancel-subscription', {
    method: 'POST',
    body: JSON.stringify({ athleteId }),
  }),
  updateSubscription: (athleteId: string, newPlan: string, billingPeriod: string) => apiCall('/stripe/update-subscription', {
    method: 'POST',
    body: JSON.stringify({ athleteId, newPlan, billingPeriod }),
  }),
};

// Health check
export const healthCheck = () => apiCall('/health');

// Dev dashboard API (only for dev.sjmate.com / localhost; backend checks DEV_ALLOWED_EMAILS)
export const devApi = {
  getOverview: () => apiCall('/dev/overview'),
  getUsers: () => apiCall('/dev/users'),
  getCoaches: () => apiCall('/dev/coaches'),
  getAthletes: () => apiCall('/dev/athletes'),
  getSubscriptions: () => apiCall('/dev/subscriptions'),
}; 